import React from 'react';
import { Typography, Button, Box } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import {
	OpeningHoursApi,
	OpeningHourObject,
	ExceptionDateObject,
	LocationOpeningHoursApi,
} from 'common/types';
import {
	getNextFreeExceptionDate,
	getDefaultExceptionDateObj,
	getDefaultTimesWithDayType,
} from './utils';
import { useTranslation } from 'services/localization/useTranslation';
import { dayTypeOptions } from './utils';

import OpeningHoursDefaultRow from './OpeningHoursDefaultRow';
import OpeningHoursExceptionRow from './OpeningHoursExceptionRow';

interface Props {
	value: OpeningHoursApi | LocationOpeningHoursApi;
	onChange: (value: OpeningHoursApi | LocationOpeningHoursApi) => void;
}

const OpeningHours = (props: Props) => {
	const { t } = useTranslation();
	const classes = useStyles();
	const { value, onChange } = props;
	const { defaultTimes, exceptionDates } = value;

	const reservedDayTypes = defaultTimes.map((openingHourObj) => openingHourObj.dayType);
	const unreservedDayTypes = dayTypeOptions.filter((dt) => !reservedDayTypes.includes(dt));
	const reservedExceptionDates = exceptionDates.map((exception) => exception.date);

	const addExceptionDate = () => {
		const newExceptionDate = getNextFreeExceptionDate(exceptionDates);

		onChange({
			...value,
			exceptionDates: [...value.exceptionDates, getDefaultExceptionDateObj(newExceptionDate)],
		});
	};

	const removeExceptionDate = (index: number) => {
		onChange({
			...value,
			exceptionDates: value.exceptionDates.filter((_, i) => i !== index),
		});
	};

	const updateExceptionDate = (index: number, data: ExceptionDateObject) => {
		onChange({
			...value,
			exceptionDates: value.exceptionDates.map((_, i) => (i === index ? data : _)),
		});
	};

	const addDefaultRow = () => {
		const dayType = unreservedDayTypes[0];

		if (dayType) {
			onChange({
				...value,
				defaultTimes: [...value.defaultTimes, getDefaultTimesWithDayType(dayType)],
			});
		}
	};

	const removeDefaultRow = (index: number) => {
		onChange({
			...value,
			defaultTimes: value.defaultTimes.filter((_, i) => i !== index),
		});
	};

	const updateDefaultRow = (index: number, data: OpeningHourObject) => {
		onChange({
			...value,
			defaultTimes: value.defaultTimes.map((_, i) => (i === index ? data : _)),
		});
	};

	return (
		<Box>
			<Box pt={3}>
				<Typography variant="body1" gutterBottom>
					{t('openingHours.openingTimes', 'Opening times')}
				</Typography>
				{defaultTimes.length === 0 && (
					<Typography variant="body2">
						{t('openingHours.open24h', 'Open 24h - add a row to specify opening times')}
					</Typography>
				)}
			</Box>
			{defaultTimes.map((row, index) => (
				<Box key={row.dayType} className={classes.row}>
					<OpeningHoursDefaultRow
						value={row}
						onChange={(data) => updateDefaultRow(index, data)}
						onDelete={() => removeDefaultRow(index)}
						reservedDayTypes={reservedDayTypes}
					/>
				</Box>
			))}
			<Box className={classes.actions}>
				<Button
					variant="outlined"
					color="primary"
					onClick={addDefaultRow}
					disabled={unreservedDayTypes.length === 0}
				>
					{t('common:actions.addRow')}
				</Button>
			</Box>
			{exceptionDates.length > 0 && (
				<Box pt={3}>
					<Typography variant="body1">{t('openingHours.exceptions', 'Exceptions')}</Typography>
				</Box>
			)}
			{exceptionDates.map((row, index) => (
				<Box key={row.date} className={classes.row}>
					<OpeningHoursExceptionRow
						value={row}
						onChange={(data) => updateExceptionDate(index, data)}
						onDelete={() => removeExceptionDate(index)}
						reservedDates={reservedExceptionDates}
					/>
				</Box>
			))}
			<Box className={classes.actions}>
				<Button variant="outlined" color="primary" onClick={addExceptionDate}>
					{t('openingHours.addException', 'Add exception')}
				</Button>
			</Box>
		</Box>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		row: {
			borderBottom: `1px solid ${theme.palette.divider}`,
			padding: theme.spacing(1.5, 0),
		},
		actions: {
			display: 'flex',
			flexDirection: 'row',
			justifyContent: 'flex-start',
			'& > *': {
				marginTop: theme.spacing(2),
				marginRight: theme.spacing(2),
			},
		},
	}),
);

export default OpeningHours;
